import React from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { connect } from 'react-redux';
import { Accessibility } from '@apps/shared/src/providerSearch/types/providerSearch';
import makeStyles from '@mui/styles/makeStyles';
import { RootState } from '../store';

const useStyles = makeStyles(theme => ({
  inputContainer: {
    marginBottom: theme.spacing(1),
  },
  label: {
    marginBottom: theme.spacing(1),
    color: '#797C80',
  },
  input: {
    background: '#fff',
    minHeight: '52px',
    borderRadius: '.75rem',
    boxShadow: 'none',
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    '& .MuiSelect-select': {
      padding: '0.5rem 1rem !important',
      width: '100% !important',
      cursor: 'pointer',
      paddingRight: '32px !important',
      backgroundColor: '#fff',
    },
    '& .MuiSelect-outlined': {
      width: '100% !important',
      backgroundColor: '#fff',
    },
    '& .MuiInputBase-input': {
      width: '100% !important',
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-input': {
      width: '100% !important',
      padding: '0.5rem 1rem !important',
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
    },
    '& .MuiSelect-icon': {
      right: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
      WebkitBoxShadow: '0 0 0 30px white inset !important',
    },
    '&:focus': {
      backgroundColor: '#fff',
    },
  },
  menuItem: {
    width: '100%',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

const mapStateToProps = ({ providerSearch }: RootState): StateProps => ({
  selectedAccessibilities: providerSearch.selectedAccessibilities,
});

type StateProps = {
  selectedAccessibilities: Accessibility[];
};

type SelectInputProps = {
  label: string;
  value: string;
  selectBoxStyle?: string;
  options: string[];
  onSelectChange: (selectedValue: string) => void;
  radiusOption?: boolean;
  accessibility?: Accessibility[];
  setAccessibility?: (value: Accessibility[]) => void;
};

type Props = StateProps & SelectInputProps;

const SelectInput: React.FC<Props> = ({
  label,
  value,
  options,
  selectBoxStyle,
  onSelectChange,
  accessibility = undefined,
  setAccessibility = undefined,
  radiusOption = false,
}) => {
  const classes = useStyles();

  const handleInputChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value as string;
    onSelectChange(newValue);
  };

  const handleCheckboxClick =
    (option: string) => (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      // Stop the propagation of the click event to avoid triggering handleInputChange
      if (radiusOption) {
        event.stopPropagation();
        if (accessibility !== undefined && setAccessibility !== undefined) {
          const updatedAccessibilities = accessibility.includes(option as Accessibility)
            ? accessibility.filter(a => a !== option)
            : [...accessibility, option];

          setAccessibility(updatedAccessibilities as Accessibility[]);
        }
      }
    };

  return (
    <div className={`${classes.inputContainer} ${selectBoxStyle || ''}`}>
      <InputLabel className={classes.label}>{label}</InputLabel>
      <Select
        value={value || ''}
        onChange={handleInputChange}
        error={false} // Update this based on your validation logic
        classes={{ root: classes.input }}
        fullWidth
        displayEmpty
        variant="outlined"
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
        sx={{
          '& .MuiSelect-select': {
            width: '100% !important',
          },
        }}
      >
        <MenuItem value="" disabled className={classes.menuItem}>
          Select
        </MenuItem>
        {options &&
          options.map(option => (
            <MenuItem
              key={option}
              value={option}
              onClick={handleCheckboxClick(option)}
              className={classes.menuItem}
            >
              {radiusOption && accessibility !== undefined ? (
                <Checkbox
                  color="primary"
                  checked={accessibility.includes(option as Accessibility)}
                  disableRipple
                />
              ) : (
                option
              )}
            </MenuItem>
          ))}
      </Select>
    </div>
  );
};

export default connect(mapStateToProps)(SelectInput);
